import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../api/auth';
import { setAuthToken } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, refreshAuth, loading: authLoading } = useAuth();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokenMissing, setTokenMissing] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setTokenMissing(true);
      setError('Oturum bilgisi bulunamadı. Lütfen email onay linkini tekrar kullanın.');
      return;
    }
    setAuthToken(token);
    localStorage.removeItem('verifiedEmail');
  }, []);

  // Eğer user zaten onboarding'i tamamlamışsa dashboard'a yönlendir
  useEffect(() => {
    if (!authLoading && user && user.is_onboarding_done) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Şifreler eşleşmiyor');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (formData.password.length < 8) {
      setError('Şifre en az 8 karakter olmalıdır');
      setLoading(false);
      return;
    }

    if (tokenMissing) {
        setLoading(false);
        return;
      }

    try {
      const response = await authApi.setPassword(formData.password);
      if (response.success) {
        localStorage.removeItem('verifiedEmail');
        
        // AuthContext'i güncelle ki user state is_onboarding_done = true olsun
        await refreshAuth();
        
        // Dashboard'a yönlendir
        navigate('/dashboard');
      } else {
        setError(response.error?.message || 'Şifre oluşturulamadı');
      }
    } catch (error: any) {
      if (error?.response?.status === 401) {
        localStorage.removeItem('authToken');
        setAuthToken(null);
        setError('Oturum süreniz doldu. Lütfen email onay linkini tekrar kullanın.');
        setTimeout(() => navigate('/login'), 2500);
      } else {
      setError(error.response?.data?.error?.message || 'Bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (tokenMissing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Oturum Bilgisi Gerekiyor</h2>
            <p className="text-gray-600 mb-6">
              Email onay token’ınız bulunamadı veya süresi dolmuş olabilir. Lütfen email onay linkini tekrar kullanın.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Giriş Sayfasına Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
            <span className="text-2xl">⚖️</span>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Şifrenizi Oluşturun
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Hoş geldiniz! Devam etmek için lütfen şifrenizi oluşturun.
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Şifre
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="En az 8 karakter"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Şifre Tekrar
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Şifrenizi tekrar girin"
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Tamamlanıyor...
                </div>
              ) : (
                'Şifre Oluştur'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
